import React, { useContext, useState, useEffect } from 'react';
import { Button, Dialog, Message } from '@alifd/next';
import {
  Form,
  FormProps,
  Input,
  InputNumber,
  Upload,
  Select,
  Radio,
  Tabs,
  Switch,
} from 'antd';
import client from '@/lib/feathers';
import { communityContext } from '../../index';
import { FormValues } from './types';
import axios from 'axios';
import {
  validatePaymentDeadLineAndGenerateDate,
  getDeadlineSetting,
} from './utils';

const { Item, useForm } = Form;
const { TabPane } = Tabs;
const SetFinanceSetting: React.FC<{
  setTrigger: React.Dispatch<React.SetStateAction<Record<string, never>>>;
}> = ({ setTrigger }) => {
  const { communityData, accountingSubjects } = useContext(communityContext);
  const [visible, setVisible] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [fileListHasChange, setFileListHasChange] = useState(false);
  const [deadlineSetting, setDeadlineSetting] = useState<{
    labels: [string, string];
    disabled: boolean;
  }>({ labels: ['同個月', '下個月'], disabled: false });
  const [showEcPay, setShowEcPay] = useState(false);

  useEffect(() => {
    if (accountingSubjects) {
      setSubjectOptions(
        accountingSubjects.map((subject) => ({
          label: subject.subjectName,
          value: subject._id,
        }))
      );
    }
  }, [accountingSubjects]);

  const [form] = useForm<FormValues>();

  useEffect(() => {
    if (visible && communityData) {
      form.setFieldsValue(communityData);
      setShowEcPay(communityData.useEcpay);
      const { autoGenerateBillDate, paymentDeadline } = communityData;
      if (autoGenerateBillDate && paymentDeadline.date) {
        const deadlineSetting = getDeadlineSetting(
          autoGenerateBillDate,
          paymentDeadline.date
        );
        setDeadlineSetting(deadlineSetting);
      }
    }
  }, [visible, communityData, form]);

  const onFinish = async (values: FormValues) => {
    const newValues = { ...values };
    try {
      if (
        fileListHasChange &&
        newValues &&
        newValues.financeSettingDocument &&
        newValues.financeSettingDocument.length > 0 &&
        newValues.financeSettingDocument[0].originFileObj
      ) {
        const formData = new FormData();
        formData.append(
          'files',
          newValues.financeSettingDocument[0].originFileObj
        );
        const res = await axios.post('/newUploads', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            authorization: localStorage.getItem('feathers-jwt'),
          },
          params: {
            type: 'communityData',
          },
        });
        newValues.financeSettingDocument[0].response = res.data;
      }
      await client.service('communityData').patch(null, newValues);
      Message.success('修改成功');
      setVisible(false);
      setTrigger({});
    } catch (err) {
      console.error(err);
    }
  };

  const onValuesChange: FormProps<FormValues>['onValuesChange'] = (
    changeValues,
    allValues
  ) => {
    setShowEcPay(changeValues.useEcpay);
    const field = Object.keys(changeValues)[0];
    if (['autoGenerateBillDate', 'paymentDeadline'].includes(field)) {
      const { autoGenerateBillDate, paymentDeadline } = allValues;
      if (autoGenerateBillDate === paymentDeadline.date) {
        form.setFieldValue(['paymentDeadline', 'type'], 'next');
      }
      setDeadlineSetting(
        getDeadlineSetting(autoGenerateBillDate, paymentDeadline.date)
      );
      form.validateFields([
        'autoGenerateBillDate',
        ['paymentDeadline', 'type'],
        ['paymentDeadline', 'date'],
      ]);
    }
  };

  const handleCancel = () => {
    setVisible(false);
    setFileListHasChange(false);
    setTrigger({});
  };

  console.log(form.getFieldValue('useEcpay'));

  return (
    <>
      {communityData && accountingSubjects && (
        <>
          <Button onClick={() => setVisible(true)}>編輯財務設定</Button>
          <Dialog
            title="編輯財務設定"
            visible={visible}
            onOk={() => form.submit()}
            onCancel={handleCancel}
            onClose={handleCancel}
          >
            <Form
              form={form}
              onFinish={onFinish}
              onValuesChange={onValuesChange}
              style={{
                width: '480px',
              }}
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 14 }}
            >
              <Tabs defaultActiveKey="1">
                <TabPane forceRender tab="財務基本設定" key="1">
                  <Item
                    label="月繳住戶管理費每坪(元)"
                    name="monthPingMoney"
                    rules={[
                      {
                        required: true,
                        message: '請輸入月繳管理費每坪(元)',
                      },
                    ]}
                  >
                    <InputNumber
                      style={{
                        width: '100%',
                      }}
                    />
                  </Item>
                  <Item
                    label="月繳店鋪管理費每坪(元)"
                    name="shopMonthPingMoney"
                    rules={[
                      {
                        required: true,
                        message: '請輸入月繳管理費每坪(元)',
                      },
                    ]}
                  >
                    <InputNumber
                      style={{
                        width: '100%',
                      }}
                    />
                  </Item>
                  <Item
                    label="月繳汽車清潔費每格(元)"
                    name="monthCarMoney"
                    rules={[
                      {
                        required: true,
                        message: '請輸入月繳汽車清潔費每格(元)',
                      },
                    ]}
                  >
                    <InputNumber
                      style={{
                        width: '100%',
                      }}
                    />
                  </Item>
                  <Item
                    label="月繳機車清潔費每格(元)"
                    name="monthScooterMoney"
                    rules={[
                      {
                        required: true,
                        message: '請輸入月繳機車清潔費每格(元)',
                      },
                    ]}
                  >
                    <InputNumber
                      style={{
                        width: '100%',
                      }}
                    />
                  </Item>
                  <Item
                    label="年繳住戶管理費每坪(元)"
                    name="yearPingMoney"
                    rules={[
                      {
                        required: true,
                        message: '請輸入年繳管理費每坪(元)',
                      },
                    ]}
                  >
                    <InputNumber
                      style={{
                        width: '100%',
                      }}
                    />
                  </Item>

                  <Item
                    label="年繳汽車清潔費每格(元)"
                    name="yearCarMoney"
                    rules={[
                      {
                        required: true,
                        message: '請輸入年繳汽車清潔費每格(元)',
                      },
                    ]}
                  >
                    <InputNumber
                      style={{
                        width: '100%',
                      }}
                    />
                  </Item>
                  <Item
                    label="年繳機車清潔費每格(元)"
                    name="yearScooterMoney"
                    rules={[
                      {
                        required: true,
                        message: '請輸入年繳機車清潔費每格(元)',
                      },
                    ]}
                  >
                    <InputNumber
                      style={{
                        width: '100%',
                      }}
                    />
                  </Item>
                  <Item
                    label="管理費收入科目"
                    name="bankId"
                    rules={[
                      {
                        required: true,
                        message: '請選擇管理費收入科目',
                      },
                    ]}
                  >
                    <Select options={subjectOptions} />
                  </Item>
                  <Item
                    label="匯款戶名"
                    name="payName"
                    rules={[
                      {
                        required: true,
                        message: '請輸入匯款戶名',
                      },
                    ]}
                  >
                    <Input />
                  </Item>
                  <Item
                    label="匯款帳號"
                    name="payId"
                    rules={[
                      {
                        required: true,
                        message: '請輸入匯款帳號',
                      },
                    ]}
                  >
                    <Input />
                  </Item>
                  <Item
                    label="超商繳費手續費(元)"
                    name="payFee"
                    rules={[
                      {
                        required: true,
                        message: '請輸入超商繳費手續費(元)',
                      },
                    ]}
                  >
                    <InputNumber
                      style={{
                        width: '100%',
                      }}
                    />
                  </Item>
                  <Item
                    name="autoGenerateBillDate"
                    label="自動產生帳單日"
                    initialValue={7}
                    rules={[
                      {
                        required: true,
                        message: '請輸入自動產生帳單日',
                      },
                      validatePaymentDeadLineAndGenerateDate,
                    ]}
                  >
                    <InputNumber
                      style={{
                        width: '100%',
                      }}
                      min={1}
                      max={28}
                    />
                  </Item>
                  <Item
                    name={['paymentDeadline', 'type']}
                    label="繳費截止日週期"
                    initialValue="current"
                    rules={[
                      {
                        required: true,
                        message: '請選擇繳費截止日週期',
                      },
                    ]}
                  >
                    <Radio.Group disabled={deadlineSetting.disabled}>
                      <Radio value="current">{deadlineSetting.labels[0]}</Radio>
                      <Radio value="next">{deadlineSetting.labels[1]}</Radio>
                    </Radio.Group>
                  </Item>
                  <Item
                    name={['paymentDeadline', 'date']}
                    label="繳費截止日"
                    initialValue={1}
                    rules={[
                      {
                        required: true,
                        message: '請輸入繳費截止日',
                      },
                      validatePaymentDeadLineAndGenerateDate,
                    ]}
                  >
                    <InputNumber
                      style={{
                        width: '100%',
                      }}
                      min={1}
                      max={28}
                    />
                  </Item>
                  <Item
                    label="財務設定文件"
                    name="financeSettingDocument"
                    valuePropName="fileList"
                    getValueFromEvent={({ fileList }) => {
                      return fileList;
                    }}
                  >
                    <Upload
                      accept=".pdf , .doc , .docx , .xls , .xlsx"
                      maxCount={1}
                      fileList={
                        form.getFieldValue('financeSettingDocument') || []
                      }
                      beforeUpload={() => false}
                      onChange={() => setFileListHasChange(true)}
                    >
                      <Button>上傳文件</Button>
                    </Upload>
                  </Item>
                  <Item
                    label="開放超商繳費"
                    name="useEcpay"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="開啟" unCheckedChildren="關閉" />
                  </Item>
                </TabPane>

                {(showEcPay || form.getFieldValue('useEcpay')) && (
                  <TabPane forceRender tab="綠界參數設定" key="2">
                    <Item
                      label="商店代號"
                      name="MerchantID"
                      rules={[
                        {
                          required: true,
                          message: '請輸入MerchantID',
                        },
                      ]}
                    >
                      <Input />
                    </Item>
                    <Item
                      label="介接HashKey"
                      name="HashKey"
                      rules={[
                        {
                          required: true,
                          message: '請輸入綠界HashKey',
                        },
                      ]}
                    >
                      <Input />
                    </Item>
                    <Item
                      label="介接HashIV"
                      name="HashIV"
                      rules={[
                        {
                          required: true,
                          message: '請輸入綠界HashIV',
                        },
                      ]}
                    >
                      <Input />
                    </Item>
                  </TabPane>
                )}
              </Tabs>
            </Form>
          </Dialog>
        </>
      )}
    </>
  );
};

export default SetFinanceSetting;
